<template>
  <div>
    <div class="tw-flex tw-justify-between">
      <div class="tw-text-xl tw-font-light tw-my-auto">Sales Tax</div>
      <div class="tw-flex">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`Tax.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/reports/tax/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
    </div>
    <filter-display v-if="showFilters === true" class="tw-my-3" :filters="filters" />
    <base-card class="tw-mb-3 tw-mt-7">
      <div class="tw-text-gray-500 tw-text-sm">
        Total Tax Collected
      </div>
      <base-skeleton-loader v-if="isLoading === true" class="tw-w-20 tw-h-6 tw-mt-1" />
      <Money-Format v-else class="tw-text-lg tw-font-normal tw-text-gray-800" :value="totalInCents" :locale="locale" currency-code="usd" subunits-value />
    </base-card>
    <div class="tw-mt-6">{{ page.total }} tax</div>
    <base-table :loading="isLoading" :fields="fields" :items="tax" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #location="{item: tax}">
        <div class="tw-whitespace-nowrap">{{ tax.businessLocationName }}</div>
      </template>
      <template #rate="{item: tax}">
        <div class="tw-flex tw-flex-col">
          <div class="tw-whitespace-nowrap">{{ tax.taxRateName.concat(" (", tax.percentage, "%)") }}</div>
          <div v-if="tax.taxRateName !== tax.jurisdiction" class="tw-whitespace-nowrap tw-text-xs tw-text-gray-500">{{ tax.jurisdiction }}</div>
        </div>
      </template>
      <template #revenue="{item: tax}">
        <Money-Format :value="tax.taxableRevenueInCents" :locale="locale" :currency-code="tax.currency" subunits-value />
      </template>
      <template #tax="{item: tax}">
        <Money-Format :value="tax.taxCollectedInCents" :locale="locale" :currency-code="tax.currency" subunits-value />
      </template>

      <template #description="{item: tip}">
        <div class="tw-whitespace-nowrap">
          <base-button type="text" @click="view(tip.userId)">{{ tip.memberName }}</base-button
          >: {{ tip.description }}
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-16" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      date-filter-label="Tip Date"
      :show-programs-filter="false"
      :show-program-groups-filter="false"
      :show-prescription-status-filter="false"
      :show-plan-status-filter="false"
      :show-plan-groups-filter="false"
      :show-plans-filter="false"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize } from "lodash";
import ExportButton from "@/components/ExportButton.vue";
import MoneyFormat from "vue-money-format";

const TAKE_KEY = "predictile_tax_report_take";

export default {
  name: "BusinessMemberTaxReport",
  components: {
    ReportFilterSlideover,
    FilterDisplay,
    FilterIcon,
    ExportButton,
    MoneyFormat,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      tax: [],
      totalInCents: 0,
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      isLoading: false,
      filters: {
        startDate: null,
        endDate: null,
        locationsFilter: [],
      },
      sortKey: "date",
      sortDesc: true,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    locale() {
      return window.navigator.language;
    },
    showFilters() {
      if (this.filters.startDate !== null || this.filters.endDate !== null || this.filters.locationsFilter.length > 0) return true;
      else return false;
    },
    fields() {
      let columns = [];
      columns.push({ title: "Location", key: "location" });
      columns.push({ title: "Tax Rate", key: "rate" });
      columns.push({ title: "Taxable Revenue", key: "revenue" });
      columns.push({ title: "Tax Collected", key: "tax" });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item["businessLocationId"];
          })
        : [];

      return {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        businessLocationIds: locationFilterStrings,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  mounted() {
    this.$refs.filterSlideOver.dateShortcutSelected("month");
    this.$refs.filterSlideOver.applyFilter();
  },
  methods: {
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
    capitalize,
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/tax/search?skip=${this.page.skip}&take=${this.page.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.tax = res.data.page.items;
          this.page.total = res.data.page.total;
          this.totalInCents = res.data.totalInCents;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load tax",
            message: "We had a problem retrieving the summary of taxes collected. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY h:mm a");
    },
    view(userId) {
      window.open(`/businesses/${this.businessId}/members/${userId}/history`, "_blank");
    },
  },
};
</script>
